import {
  all,
  call,
  put,
  takeLatest,
  takeEvery,
} from '@redux-saga/core/effects';
import {
  INTEGRATION_CRITERIA_TYPE,
  INTEGRATION_SETTING_TYPE,
  INTEGRATION_SYNC_TYPE,
  INTEGRATION_TYPE,
  NA,
} from '../../../common/constants/IntegrationConstants';
import {
  COMMON_SYNC_DIRECTION,
  CONFIG_TYPES,
  DATA_MAPPING_TYPES,
  ENTITY_SYNC_STATUS,
  SYNC_DIRECTION,
} from '../../../common/constants/integrationConstants/crmConstants';
import { request } from '../../../common/util/RestClient';
import envConfig from '../../../config';
import {
  createAdditionalDeduplicationMapping,
  createCondition,
  createDataMapping,
  createDeduplicationMapping,
  createInSyncFilter,
  fetchAdditionalDeduplicationMapping,
  fetchAdditionalDeduplicationStatus,
  fetchCriterias,
  fetchCrmActivityConfigs,
  fetchCrmActivityMapping,
  fetchCrmConfigStatus,
  fetchCrmDataMappings,
  fetchCrmDeduplicationMappings,
  fetchCrmInSyncFilters,
  fetchCrmObjectProperties,
  fetchDeduplicationUpdateStatus,
  fetchNoteSyncStatus,
  fetchParentCreateStatus,
  fetchParentLinkConfigStatus,
  fetchPipelineMapping,
  fetchSyncDataStatus,
  saveAdditionalDeduplicationStatus,
  saveCrmActivitySyncConfig,
  saveCrmConfigStatus,
  saveDeduplicationUpdateStatus,
  saveNoteSyncStatus,
  saveParentCreateStatus,
  saveParentLinkConfigStatus,
  savePipelineMapping,
  triggerSyncDataStatus,
} from '../../routines/integrations/crmRoutines';
import { capitalizeFirstLetter } from '../../../common/util/commonUtil';

function* fetchPropertiesSaga(action) {
  try {
    const { vendor } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/properties`;
    const { data } = yield call(request, url);
    yield put(fetchCrmObjectProperties.success({ ...data }));
  } catch (error) {
    yield put(fetchCrmObjectProperties.failure({ error }));
  }
}

function* fetchDataMappingsSaga(action) {
  try {
    const { vendor } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/configs/parameter-mapping`;
    const { data } = yield call(request, url);
    const {
      velarisToCrm,
      crmToVelaris,
      deduplication,
      entityInSync: inSync,
      customParentLinkDeduplication,
      dealRecordIdDeduplication,
    } = data;
    yield put(
      fetchCrmDataMappings.success({
        into: crmToVelaris,
        out: velarisToCrm,
        mappingType: CONFIG_TYPES.DATA_MAPPING,
      })
    );
    yield put(
      fetchCrmDataMappings.success({
        inSync,
        mappingType: CONFIG_TYPES.IN_SYNC,
      })
    );
    yield put(
      fetchCrmDataMappings.success({
        customParentLinkDeduplication,
        mappingType: CONFIG_TYPES.PARENT_LINK,
      })
    );

    yield put(fetchCrmDeduplicationMappings.success(deduplication));
    yield put(
      fetchAdditionalDeduplicationMapping.success(dealRecordIdDeduplication)
    );
  } catch (error) {
    yield put(fetchCrmDataMappings.failure({ error }));
    yield put(fetchCrmDeduplicationMappings.failure({ error }));
    yield put(fetchAdditionalDeduplicationMapping.failure({ error }));
  }
}

function* fetchCriteriasSaga(action) {
  try {
    const { vendor } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/configs/criteria`;
    const { data } = yield call(request, url);
    yield put(fetchCriterias.success({ ...data }));
  } catch (error) {
    yield put(fetchCriterias.failure({ error }));
  }
}

function* fetchInSyncFilterSaga(action) {
  try {
    const { vendor } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/configs/filters`;
    const { data } = yield call(request, url);
    yield put(fetchCrmInSyncFilters.success({ ...data }));
  } catch (error) {
    yield put(fetchCrmInSyncFilters.failure({ error }));
  }
}

function* fetchCrmConfigSaga(action) {
  const { vendor } = action.payload;
  try {
    const url = `${envConfig.APIG_URL}integration/integration-context?vendor=${vendor}`;
    const { data } = yield call(request, url);
    yield put(fetchNoteSyncStatus.success({ ...data }));
    yield put(fetchCrmConfigStatus.success({ ...data }));
    yield put(
      fetchCrmActivityConfigs.success({ vendor, configs: { ...data } })
    );
    yield put(fetchParentCreateStatus.success({ ...data }));
    yield put(fetchDeduplicationUpdateStatus.success({ ...data }));
    yield put(fetchAdditionalDeduplicationStatus.success({ ...data }));
  } catch (error) {
    yield put(fetchNoteSyncStatus.failure({ error }));
    yield put(fetchCrmConfigStatus.failure({ error }));
    yield put(fetchCrmActivityConfigs.failure({ error }));
    yield put(fetchParentCreateStatus.failure({ error }));
    yield put(fetchDeduplicationUpdateStatus.failure({ error }));
    yield put(fetchAdditionalDeduplicationStatus.failure({ error }));
  }
}

function* fetchCrmParentLinkConfigSaga(action) {
  const { vendor } = action.payload;
  try {
    const url = `${envConfig.APIG_URL}crm/${vendor}/integration-config`;
    const { data } = yield call(request, url);
    yield put(fetchParentLinkConfigStatus.success({ ...data }));
  } catch (error) {
    yield put(fetchParentLinkConfigStatus.failure({ error }));
  }
}

function* saveCondtionSaga(action) {
  try {
    const { vendor, entityLevel, objectType, criteria, conditions } =
      action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/configs/criteria`;
    const config = {
      method: 'POST',
      payload: {
        entityLevel,
        objectType,
        criteria,
        conditions,
      },
    };

    const { data } = yield call(request, url, config);
    yield put(
      createCondition.success({
        ...data,
        successMessage: 'Hubspot configuration successfully saved',
      })
    );
  } catch (error) {
    yield put(createCondition.failure({ error }));
  }
}
function* saveInSyncFilterSaga(action) {
  try {
    const { vendor, entityLevel, id, filters } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/configs/filters`;
    const config = {
      method: 'POST',
      payload: {
        entityLevel,
        filters,
        id,
      },
    };

    const { data } = yield call(request, url, config);
    yield put(
      createInSyncFilter.success({
        ...data,
        successMessage: 'Hubspot configuration successfully saved',
      })
    );
  } catch (error) {
    yield put(createInSyncFilter.failure({ error }));
  }
}
function* saveDataMappingSaga(action) {
  try {
    const {
      vendor,
      entityLevel: entity,
      criteria,
      id,
      type,
      mapping,
      syncDirection,
      mappingType,
    } = action.payload;
    const updatedMapping = Object.keys(mapping).map((key) => {
      const dataMappingObj = mapping[key];
      return {
        crm: {
          ...dataMappingObj.crm,
          propertyType: dataMappingObj.crmPropertyType?.fieldId,
          ...(dataMappingObj.fieldConfig
            ? { fieldConfig: dataMappingObj.fieldConfig }
            : {}),
        },
        velaris: dataMappingObj.velaris,
      };
    });

    const url = `${envConfig.APIG_URL}crm/${vendor}/configs/parameter-mapping`;
    const config = {
      method: 'POST',
      payload: {
        entity,
        criteria,
        id,
        type,
        mapping: updatedMapping,
      },
    };
    const { data } = yield call(request, url, config);
    data.mapping = mapping;
    yield put(
      createDataMapping.success({
        ...data,
        mappingType,
        syncDirection,
        successMessage: 'Hubspot configuration successfully saved',
      })
    );
  } catch (error) {
    yield put(createDataMapping.failure({ error }));
  } finally {
    yield put(createDataMapping.fulfill());
  }
}

function* saveDeduplicationMappingSaga(action) {
  try {
    const { vendor, entityLevel, id, mapping } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/configs/parameter-mapping`;

    const transformedMapping = [
      {
        crm: {
          fieldId: mapping[0].crm.fieldId,
          propertyType: mapping[0].crmPropertyType.fieldId,
        },
        velaris: { fieldId: mapping[0].velaris.fieldId },
      },
    ];
    const config = {
      method: 'POST',
      payload: {
        entity: entityLevel,
        criteria: 'all',
        id,
        type: DATA_MAPPING_TYPES.DEDUPLICATION,
        mapping: transformedMapping,
      },
    };
    const { data } = yield call(request, url, config);
    yield put(
      createDeduplicationMapping.success({
        ...data,
        successMessage: 'Hubspot configuration successfully saved',
      })
    );
  } catch (error) {
    yield put(createDeduplicationMapping.failure({ error }));
  } finally {
    yield put(createDeduplicationMapping.fulfill());
  }
}

function* saveRecordIdMappingSaga(action) {
  try {
    const { vendor, entityLevel, id, mapping } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/configs/parameter-mapping`;

    const transformedMapping = [
      {
        crm: {
          fieldId: mapping[0].crm.fieldId,
          propertyType: mapping[0].crmPropertyType.fieldId,
        },
        velaris: { fieldId: mapping[0].velaris.fieldId },
      },
    ];
    const config = {
      method: 'POST',
      payload: {
        entity: entityLevel,
        criteria: 'all',
        id,
        type: DATA_MAPPING_TYPES.DAILY_SYNC_RECORD_ID_MAPPING,
        mapping: transformedMapping,
      },
    };
    const { data } = yield call(request, url, config);
    yield put(
      createAdditionalDeduplicationMapping.success({
        ...data,
        successMessage: 'Hubspot configuration successfully saved',
      })
    );
  } catch (error) {
    yield put(createAdditionalDeduplicationMapping.failure({ error }));
  } finally {
    yield put(createAdditionalDeduplicationMapping.fulfill());
  }
}

function* saveNoteSyncSaga(action) {
  try {
    const { integrationConfigs, entityLevel } = action.payload;
    const url = `${envConfig.APIG_URL}integration/integration-context`;

    const config = {
      method: 'PUT',
      payload: {
        integrationConfigs,
      },
    };

    yield call(request, url, config);
    yield put(
      saveNoteSyncStatus.success({
        integrationConfigs,
        entityLevel,
        successMessage: 'Hubspot configuration successfully saved',
      })
    );
  } catch (error) {
    yield put(saveNoteSyncStatus.failure({ error }));
  } finally {
    yield put(saveNoteSyncStatus.fulfill());
  }
}

function* saveConfigStatusSaga(action) {
  try {
    const {
      vendor,
      entityLevel,
      criteria,
      syncDirection,
      enable,
      syncType,
      settingType,
    } = action.payload;
    const url = `${envConfig.APIG_URL}integration/integration-context`;
    const direction =
      syncDirection === SYNC_DIRECTION.OUT
        ? COMMON_SYNC_DIRECTION.OUT
        : COMMON_SYNC_DIRECTION.IN;

    const config = {
      method: 'PUT',
      payload: {
        integrationConfigs: [
          {
            syncDirection: direction,
            integrationType: INTEGRATION_TYPE.CRM,
            vendor,
            settingType,
            syncType,
            entityLevel,
            criteria,
            enable,
          },
        ],
      },
    };

    yield call(request, url, config);
    yield put(
      saveCrmConfigStatus.success({
        entityLevel,
        settingType,
        criteria,
        syncType,
        enable,
      })
    );
  } catch (error) {
    yield put(saveCrmConfigStatus.failure({ error }));
  } finally {
    yield put(saveCrmConfigStatus.fulfill());
  }
}

function* saveParentLinkConfigSaga(action) {
  try {
    const {
      vendor,
      entityLevel,
      criteria,
      linkParentType,
      syncType,
      parentLinkEnable,
    } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/integration-config`;
    const enable = parentLinkEnable ? 1 : 0;
    const config = {
      method: 'POST',
      payload: {
        syncDirection: COMMON_SYNC_DIRECTION.IN,
        integrationType: INTEGRATION_TYPE.CRM,
        settingType: INTEGRATION_SETTING_TYPE.PARENT_LINK,
        syncType,
        linkParentType,
        entityLevel,
        criteria,
        enable,
      },
    };

    yield call(request, url, config);
    yield put(
      saveParentLinkConfigStatus.success({
        criteria,
        enable,
      })
    );
  } catch (error) {
    yield put(saveParentLinkConfigStatus.failure({ error }));
  } finally {
    yield put(saveParentLinkConfigStatus.fulfill());
  }
}

function* saveParentCreateSaga(action) {
  try {
    const { vendor, entityLevel, criteria, enable, syncType } = action.payload;
    const url = `${envConfig.APIG_URL}integration/integration-context`;
    const parentCreateEnable = enable ? 1 : 0;

    const config = {
      method: 'PUT',
      payload: {
        integrationConfigs: [
          {
            syncDirection: COMMON_SYNC_DIRECTION.IN,
            integrationType: INTEGRATION_TYPE.CRM,
            vendor,
            settingType: INTEGRATION_SETTING_TYPE.PARENT_LINK_CREATE,
            syncType,
            entityLevel,
            criteria,
            enable: parentCreateEnable,
          },
        ],
      },
    };

    yield call(request, url, config);
    yield put(
      saveParentCreateStatus.success({
        criteria,
        enable: parentCreateEnable,
      })
    );
  } catch (error) {
    yield put(saveParentCreateStatus.failure({ error }));
  } finally {
    yield put(saveParentCreateStatus.fulfill());
  }
}

function* saveDeduplicationUpdateSaga(action) {
  try {
    const { vendor, entityLevel, enable } = action.payload;
    const url = `${envConfig.APIG_URL}integration/integration-context`;
    const deduplicationUpdateEnable = enable ? 1 : 0;

    const config = {
      method: 'PUT',
      payload: {
        integrationConfigs: [
          {
            syncDirection: COMMON_SYNC_DIRECTION.IN,
            integrationType: INTEGRATION_TYPE.CRM,
            vendor,
            settingType: INTEGRATION_SETTING_TYPE.DEDUPLICATION_UPDATE,
            syncType: INTEGRATION_SYNC_TYPE.NA,
            entityLevel,
            criteria: INTEGRATION_CRITERIA_TYPE.NA,
            enable: deduplicationUpdateEnable,
          },
        ],
      },
    };

    yield call(request, url, config);
    yield put(
      saveDeduplicationUpdateStatus.success({
        enable: deduplicationUpdateEnable,
      })
    );
  } catch (error) {
    yield put(saveDeduplicationUpdateStatus.failure({ error }));
  } finally {
    yield put(saveDeduplicationUpdateStatus.fulfill());
  }
}

function* savesaveAdditionalDeduplicationSaga(action) {
  try {
    const { vendor, entityLevel, enable } = action.payload;
    const url = `${envConfig.APIG_URL}integration/integration-context`;
    const additionalDeduplicationEnable = enable ? 1 : 0;

    const config = {
      method: 'PUT',
      payload: {
        integrationConfigs: [
          {
            syncDirection: NA,
            integrationType: INTEGRATION_TYPE.CRM,
            vendor,
            settingType: INTEGRATION_SETTING_TYPE.ADDITIONAL_DEDUPLICATION,
            syncType: INTEGRATION_SYNC_TYPE.NA,
            entityLevel,
            criteria: INTEGRATION_CRITERIA_TYPE.NA,
            enable: additionalDeduplicationEnable,
          },
        ],
      },
    };

    yield call(request, url, config);
    yield put(
      saveAdditionalDeduplicationStatus.success({
        enable: additionalDeduplicationEnable,
        entityLevel,
      })
    );
  } catch (error) {
    yield put(saveAdditionalDeduplicationStatus.failure({ error }));
  } finally {
    yield put(saveAdditionalDeduplicationStatus.fulfill());
  }
}

function* fetchCrmActivityMappingSaga(action) {
  try {
    const { vendor } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/activity_field_mapping`;
    const { data } = yield call(request, url);
    yield put(
      fetchCrmActivityMapping.success({ vendor, mapping: { ...data } })
    );
  } catch (error) {
    yield put(fetchCrmActivityMapping.failure({ error }));
  }
}

function* saveCrmActivityMappingSaga(action) {
  try {
    const { vendor, integrationConfigs, activityMapping } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/activity_field_mapping`;
    const config = {
      method: 'POST',
      payload: {
        integrationConfigs,
        mapping: activityMapping,
      },
    };
    const { data } = yield call(request, url, config);
    yield put(
      saveCrmActivitySyncConfig.success({
        vendor,
        config: { ...data },
        successMessage: `${capitalizeFirstLetter(
          vendor
        )} configuration successfully saved`,
      })
    );
  } catch (error) {
    yield put(saveCrmActivitySyncConfig.failure({ error }));
  }
}

function* fetchSyncDataStatusSaga(action) {
  const { vendor } = action.payload;
  try {
    const url = `${envConfig.APIG_URL}crm/${vendor}/daily_sync_trigger?interval=manual`;
    const { data } = yield call(request, url);
    yield put(fetchSyncDataStatus.success(data));
  } catch (error) {
    yield put(fetchSyncDataStatus.failure({ error }));
  }
}

function* triggerSyncDataStatusSaga(action) {
  try {
    const { vendor, level } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/daily_sync_trigger`;
    const config = {
      method: 'POST',
      payload: {
        level,
        triggerSync: ENTITY_SYNC_STATUS.STARTED,
      },
    };
    yield call(request, url, config);
    yield put(
      triggerSyncDataStatus.success({
        level,
        status: ENTITY_SYNC_STATUS.IN_PROGRESS,
        successMessage: `${capitalizeFirstLetter(
          vendor
        )} sync data successfully triggered`,
      })
    );
  } catch (error) {
    yield put(triggerSyncDataStatus.failure({ error }));
  }
}
function* fetchPipelineMappingSaga(action) {
  try {
    const { vendor } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/pipeline_mapping`;
    const { data } = yield call(request, url);
    yield put(fetchPipelineMapping.success(data));
  } catch (error) {
    yield put(fetchPipelineMapping.failure({ error }));
  }
}

function* saveCrmPipelineMappingSaga(action) {
  try {
    const { vendor, pipelineType, pipelineMappings } = action.payload;
    const url = `${envConfig.APIG_URL}crm/${vendor}/pipeline_mapping`;
    const config = {
      method: 'POST',
      payload: {
        pipelineMappings,
        pipelineType,
      },
    };
    const { data } = yield call(request, url, config);
    yield put(
      savePipelineMapping.success({
        data,
        successMessage: `${capitalizeFirstLetter(
          vendor
        )} configuration successfully saved`,
      })
    );
  } catch (error) {
    yield put(savePipelineMapping.failure({ error }));
  }
}
export default function* root() {
  yield all([
    takeLatest(fetchCrmObjectProperties.TRIGGER, fetchPropertiesSaga),
    takeLatest(fetchCrmDataMappings.TRIGGER, fetchDataMappingsSaga),
    takeLatest(fetchCriterias.TRIGGER, fetchCriteriasSaga),
    takeLatest(fetchCrmInSyncFilters.TRIGGER, fetchInSyncFilterSaga),
    takeLatest(fetchNoteSyncStatus.TRIGGER, fetchCrmConfigSaga),
    takeLatest(
      fetchParentLinkConfigStatus.TRIGGER,
      fetchCrmParentLinkConfigSaga
    ),
    takeLatest(createDataMapping.TRIGGER, saveDataMappingSaga),
    takeLatest(
      createDeduplicationMapping.TRIGGER,
      saveDeduplicationMappingSaga
    ),
    takeLatest(createCondition.TRIGGER, saveCondtionSaga),
    takeLatest(createInSyncFilter.TRIGGER, saveInSyncFilterSaga),
    takeLatest(saveNoteSyncStatus.TRIGGER, saveNoteSyncSaga),
    takeEvery(saveCrmConfigStatus.TRIGGER, saveConfigStatusSaga),
    takeLatest(saveParentLinkConfigStatus.TRIGGER, saveParentLinkConfigSaga),
    takeLatest(fetchCrmActivityMapping.TRIGGER, fetchCrmActivityMappingSaga),
    takeLatest(saveCrmActivitySyncConfig.TRIGGER, saveCrmActivityMappingSaga),
    takeLatest(fetchParentCreateStatus.TRIGGER, fetchCrmConfigSaga),
    takeLatest(saveParentCreateStatus.TRIGGER, saveParentCreateSaga),
    takeLatest(fetchDeduplicationUpdateStatus.TRIGGER, fetchCrmConfigSaga),
    takeLatest(
      saveDeduplicationUpdateStatus.TRIGGER,
      saveDeduplicationUpdateSaga
    ),
    takeLatest(
      saveAdditionalDeduplicationStatus.TRIGGER,
      savesaveAdditionalDeduplicationSaga
    ),
    takeLatest(
      createAdditionalDeduplicationMapping.TRIGGER,
      saveRecordIdMappingSaga
    ),
    takeLatest(fetchSyncDataStatus.TRIGGER, fetchSyncDataStatusSaga),
    takeLatest(triggerSyncDataStatus.TRIGGER, triggerSyncDataStatusSaga),
    takeLatest(fetchPipelineMapping.TRIGGER, fetchPipelineMappingSaga),
    takeLatest(savePipelineMapping.TRIGGER, saveCrmPipelineMappingSaga),
  ]);
}
